import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import AddIcon from '@mui/icons-material/Add'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import {
    ESTADOS_COBRANCA,
    ESTADO_COBRANCA_LABEL,
    ESTADO_COBRANCA_TONE,
    formatDate,
    formatMoney,
    type Cobranca,
    type EstadoCobranca,
} from '../subscricoesModel'
import CobrancaFormDialog from '../components/CobrancaFormDialog'
import CobrancaDetailDialog from '../components/CobrancaDetailDialog'

const FILTROS: (EstadoCobranca | 'TODAS')[] = ['TODAS', ...ESTADOS_COBRANCA]

export default function CobrancasList() {
    const { notify } = useNotification()

    const [rows, setRows] = useState<Cobranca[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-cobrancas:table-page', 20)
    const [estado, setEstado] = useState<EstadoCobranca | 'TODAS'>('TODAS')
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const [formOpen, setFormOpen] = useState(false)
    const [detailId, setDetailId] = useState<string | null>(null)

    const fetch = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Page<Cobranca>>('/api/admin/subscricoes/cobrancas', {
            params: {
                page,
                size: rowsPerPage,
                sort: 'emitidaEm,desc',
                ...(estado !== 'TODAS' ? { estado } : {}),
            },
        })
            .then((r) => {
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => setError('Não foi possível carregar as cobranças.'))
            .finally(() => setIsLoading(false))
    }, [page, rowsPerPage, estado])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch alterna a sua própria flag de loading
        fetch()
    }, [fetch])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Cobranças</Typography>
                    <Typography variant="body2" color="text.secondary">{total} {total === 1 ? 'cobrança' : 'cobranças'}</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>Nova cobrança</Button>
            </Box>

            <ToggleButtonGroup
                size="small"
                exclusive
                value={estado}
                onChange={(_, v) => { if (v) { setEstado(v); setPage(0) } }}
                sx={{ mb: 2, flexWrap: 'wrap' }}
            >
                {FILTROS.map((f) => (
                    <ToggleButton key={f} value={f} sx={{ textTransform: 'none' }}>
                        {f === 'TODAS' ? 'Todas' : ESTADO_COBRANCA_LABEL[f]}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Referência</TableCell>
                                <TableCell>Empresa</TableCell>
                                <TableCell>Período</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell align="right">Em dívida</TableCell>
                                <TableCell>Vencimento</TableCell>
                                <TableCell>Estado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Nenhuma cobrança.</TableCell></TableRow>
                            ) : (
                                rows.map((c) => (
                                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(c.id)}>
                                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{c.referencia}</TableCell>
                                        <TableCell>{c.tenantNome ?? c.tenantId}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                            {formatDate(c.periodoInicio)} – {formatDate(c.periodoFim)}
                                        </TableCell>
                                        <TableCell align="right">{formatMoney(c.valor)}</TableCell>
                                        <TableCell align="right">{formatMoney(c.emDivida)}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(c.vencimentoEm)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={ESTADO_COBRANCA_LABEL[c.estado]} sx={{ fontWeight: 600, ...ESTADO_COBRANCA_TONE[c.estado] }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>

            <CobrancaFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSaved={() => { setFormOpen(false); notify('Cobrança criada.'); fetch() }}
            />

            <CobrancaDetailDialog
                cobrancaId={detailId}
                onClose={() => setDetailId(null)}
                onChanged={fetch}
            />
        </Box>
    )
}
